import { PAPEIS_NO_CLIENTE, type Membro, type MembroComConvite, type PapelNoCliente } from "@base/contracts";
import {
  Button,
  CabecalhoDePagina,
  CampoDeSelecao,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EstadoCarregando,
  EstadoDeErro,
  EstadoVazio,
  Etiqueta,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabela,
  type ColunaDaTabela,
} from "@base/ui";
import { MoreHorizontal, UserPlus, Users } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { FILTRO_DA_CONTA_VAZIO, coincideComFiltroDaConta, type FiltroDaConta } from "../../../shared/lista/filtro-da-conta";
import { BarraDeFiltrosDaLista } from "../../../shared/ui/barra-de-filtros-da-lista";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { useClienteSelecionado } from "../application/clientes.contexto";
import {
  useAdicionarMembro,
  useAlterarPapelDoMembro,
  useEnviarLinkDeSenhaDoMembro,
  useMembros,
  useRemoverMembro,
} from "../application/use-clientes";
import { DialogoDoLinkDeSenha } from "../../../shared/ui/campo-de-link-copiavel";
import { ROTULO_DO_PAPEL_NO_CLIENTE } from "../domain/papeis-no-cliente";

const OPCOES_DE_PAPEL = PAPEIS_NO_CLIENTE.map((papel) => ({
  valor: papel,
  rotulo: ROTULO_DO_PAPEL_NO_CLIENTE[papel],
}));

/**
 * Pessoas com acesso ao cliente selecionado (equipe Monte e gestor da
 * clinica): dar acesso, trocar o cargo e remover. Membro nao ve esta tela.
 */
export function PaginaDePessoas() {
  const { selecionado } = useClienteSelecionado();
  if (!selecionado) {
    return (
      <EstadoVazio
        titulo="Selecione um cliente"
        descricao="Escolha a clínica no seletor da barra lateral para ver quem tem acesso a ela."
      />
    );
  }
  return <PessoasDoCliente key={selecionado.id} clienteId={selecionado.id} nome={selecionado.nome} />;
}

function PessoasDoCliente({ clienteId, nome }: { readonly clienteId: string; readonly nome: string }) {
  const membros = useMembros(clienteId);
  const [adicionando, setAdicionando] = useState(false);
  const [filtro, setFiltro] = useState<FiltroDaConta>(FILTRO_DA_CONTA_VAZIO);
  const [convite, setConvite] = useState<{ email: string; link: string } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const todos = membros.data?.dados ?? [];
  const visiveis = useMemo(() => todos.filter((membro) => coincideComFiltroDaConta(membro, filtro)), [todos, filtro]);

  return (
    <div className="flex flex-col gap-secao">
      <CabecalhoDePagina
        rotulo={nome}
        titulo="Acessos"
        descricao="Quem da clínica acessa o painel. Gestor: acessa tudo da clínica, inclusive configurações e acessos. Membro: só visualiza."
        acoes={
          <Button onClick={() => setAdicionando(true)}>
            <UserPlus aria-hidden="true" />
            Dar acesso
          </Button>
        }
      />

      <p
        role="status"
        aria-live="polite"
        className={aviso ? "rounded-xl bg-accent px-4 py-3 text-sm text-accent-foreground" : "sr-only"}
      >
        {aviso}
      </p>

      {membros.isPending ? (
        <EstadoCarregando rotulo="Carregando pessoas" />
      ) : membros.isError ? (
        <EstadoDeErro mensagem={mensagemDoErro(membros.error)} aoTentarNovamente={() => void membros.refetch()} />
      ) : todos.length === 0 ? (
        <EstadoVazio
          titulo="Ninguém da clínica tem acesso ainda"
          descricao="Dê acesso ao gestor da clínica para começar."
          icone={<Users className="size-6" aria-hidden="true" />}
        />
      ) : (
        <>
          <BarraDeFiltrosDaLista
            valor={filtro}
            aoMudar={setFiltro}
            papeis={OPCOES_DE_PAPEL}
          />
          {visiveis.length === 0 ? (
            <EstadoVazio
              titulo="Nenhum resultado"
              descricao="Tente outro nome, e-mail ou filtro."
              icone={<Users className="size-6" aria-hidden="true" />}
            />
          ) : (
            <Tabela
              legenda={`Pessoas com acesso a ${nome}`}
              colunas={colunas(clienteId, setAviso)}
              itens={visiveis}
              chaveDe={(membro) => membro.usuarioId}
            />
          )}
        </>
      )}

      <Dialog open={adicionando} onOpenChange={setAdicionando}>
        <DialogContent tamanho="formulario">
          <DialogHeader rotulo="Acessos">
            <DialogTitle>Dar acesso a {nome}</DialogTitle>
            <DialogDescription>
              Quem ainda não tem conta recebe um link para criar a senha. Copie e envie para a pessoa.
            </DialogDescription>
          </DialogHeader>
          {adicionando ? (
            <FormularioDeAcesso
              clienteId={clienteId}
              aoAdicionar={(membro) => {
                setAdicionando(false);
                if (membro.linkDeSenha) {
                  setConvite({ email: membro.email, link: membro.linkDeSenha });
                }
                setAviso(
                  membro.senhaDefinida
                    ? `${membro.nome} agora acessa ${nome}.`
                    : `Acesso criado para ${membro.email}. Copie o link e envie para a pessoa.`,
                );
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <DialogoDoLinkDeSenha
        aberto={convite !== null}
        titulo="Link do convite"
        descricao={`Copie e envie para ${convite?.email}. Vale 72 horas.`}
        link={convite?.link ?? null}
        aoFechar={() => setConvite(null)}
      />
    </div>
  );
}

function FormularioDeAcesso({
  clienteId,
  aoAdicionar,
}: {
  readonly clienteId: string;
  readonly aoAdicionar: (membro: MembroComConvite) => void;
}) {
  const adicionar = useAdicionarMembro(clienteId);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [papel, setPapel] = useState<PapelNoCliente>("membro");

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    adicionar.mutate(
      { nome: nome.trim(), email: email.trim().toLowerCase(), papel },
      { onSuccess: aoAdicionar },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-6">
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="acesso-nome">Nome</FieldLabel>
          <Input id="acesso-nome" autoComplete="off" value={nome} onChange={(e) => setNome(e.target.value)} />
        </Field>
        <Field>
          <FieldLabel htmlFor="acesso-email">E-mail</FieldLabel>
          <Input
            id="acesso-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <FieldDescription>Se a pessoa já atende outra clínica, use o mesmo e-mail.</FieldDescription>
        </Field>
        <CampoDeSelecao
          rotulo="Papel na clínica"
          opcoes={OPCOES_DE_PAPEL}
          value={papel}
          onChange={(evento) => setPapel(evento.target.value as PapelNoCliente)}
        />
        {adicionar.isError ? <FieldError>{mensagemDoErro(adicionar.error)}</FieldError> : null}
        <Button type="submit" disabled={adicionar.isPending || nome.trim().length < 3 || !email}>
          {adicionar.isPending ? "Enviando…" : "Dar acesso"}
        </Button>
      </FieldGroup>
    </form>
  );
}

function colunas(clienteId: string, avisar: (mensagem: string) => void): readonly ColunaDaTabela<Membro>[] {
  return [
    {
      chave: "nome",
      titulo: "Pessoa",
      celula: (membro) => (
        <div className="flex flex-col">
          <span className="font-medium">{membro.nome}</span>
          <span className="text-xs text-muted-foreground">{membro.email}</span>
        </div>
      ),
    },
    {
      chave: "papel",
      titulo: "Papel",
      celula: (membro) => (
        <Etiqueta tom={membro.papel === "gestor" ? "secundario" : "contorno"}>
          {ROTULO_DO_PAPEL_NO_CLIENTE[membro.papel]}
        </Etiqueta>
      ),
    },
    {
      chave: "situacao",
      titulo: "Situação",
      ocultarNoCelular: true,
      celula: (membro) =>
        !membro.ativo ? (
          <Etiqueta tom="neutro">Conta desativada</Etiqueta>
        ) : membro.senhaDefinida ? (
          <Etiqueta tom="sucesso">Ativo</Etiqueta>
        ) : (
          <Etiqueta tom="atencao">Convite pendente</Etiqueta>
        ),
    },
    {
      chave: "acoes",
      titulo: "Ações",
      celula: (membro) => <AcoesDoMembro clienteId={clienteId} membro={membro} avisar={avisar} />,
    },
  ];
}

function AcoesDoMembro({
  clienteId,
  membro,
  avisar,
}: {
  readonly clienteId: string;
  readonly membro: Membro;
  readonly avisar: (mensagem: string) => void;
}) {
  const alterar = useAlterarPapelDoMembro(clienteId);
  const remover = useRemoverMembro(clienteId);
  const enviarLink = useEnviarLinkDeSenhaDoMembro(clienteId);
  const [link, setLink] = useState<string | null>(null);
  const outroPapel: PapelNoCliente = membro.papel === "gestor" ? "membro" : "gestor";

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={alterar.isPending || remover.isPending || enviarLink.isPending}
          aria-label={`Ações para ${membro.nome}`}
        >
          <MoreHorizontal aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {membro.ativo ? (
          <DropdownMenuItem
            onSelect={() =>
              enviarLink.mutate(membro.usuarioId, {
                onSuccess: (resposta) => {
                  setLink(resposta.linkDeSenha);
                  avisar(`Link gerado. Copie e envie para ${membro.email}.`);
                },
                onError: (falha) => avisar(mensagemDoErro(falha)),
              })
            }
          >
            {membro.senhaDefinida ? "Redefinir senha" : "Gerar link do convite"}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onSelect={() =>
            alterar.mutate(
              { usuarioId: membro.usuarioId, papel: outroPapel },
              {
                onSuccess: () =>
                  avisar(`${membro.nome} agora é ${ROTULO_DO_PAPEL_NO_CLIENTE[outroPapel].toLowerCase()}.`),
                onError: (falha) => avisar(mensagemDoErro(falha)),
              },
            )
          }
        >
          Tornar {ROTULO_DO_PAPEL_NO_CLIENTE[outroPapel].toLowerCase()}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={() =>
            remover.mutate(membro.usuarioId, {
              onSuccess: () => avisar(`${membro.nome} não acessa mais este cliente.`),
              onError: (falha) => avisar(mensagemDoErro(falha)),
            })
          }
        >
          Remover acesso
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
      <DialogoDoLinkDeSenha
        aberto={link !== null}
        titulo={membro.senhaDefinida ? "Link de redefinição" : "Link do convite"}
        descricao={`Copie e envie para ${membro.email}. Vale 72 horas.`}
        link={link}
        aoFechar={() => setLink(null)}
      />
    </>
  );
}
